import { useState } from 'react';
import { useMutation } from 'react-query';
import { Alert, Box, Button, Collapse, Divider, Stack } from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import {
  dinnerClaimErrorNote,
  dinnerClaimResultNote,
  type DinnerClaimNote,
} from '../../../lib/dinnerClaim';
import { createdInviteeId, dinnerGuestPlan, unseatedGuestNote } from '../../../lib/dinnerGuests';
import DinnerGuestPicker, { type NewDinnerGuest } from './DinnerGuestPicker';
import type {
  DinnerClaimResult,
  GroupDinnerHold,
  GroupInvitee,
  GroupInviteeInput,
} from '../../../types/groups';

/** Who to seat, and anything the host should know about how we found them. */
interface SeatTarget {
  inviteeId: string;
  guestName: string;
  /** Prepended to the outcome, e.g. "X was already on this trip". */
  preface?: string;
}

/** The result of resolving a typed-in guest to a roster row. */
interface AddOutcome {
  rosterChanged: boolean;
  seat?: SeatTarget;
  note?: DinnerClaimNote;
}

const plainNote = (severity: DinnerClaimNote['severity'], message: string): DinnerClaimNote =>
  ({ severity, message, status: '' });

/**
 * The host's way to put somebody at a dinner table.
 *
 * It exists for the guests who cannot do it themselves. A guest claims a seat
 * from their own trip page, which needs either a sign-in or a forwarded invite
 * link — so a guest with no email and no phone on file can never reach any
 * guest-facing claim path at all, and until now the fallback copy on their page
 * ("ask the hosts to save you a seat") pointed at an endpoint Studio could not
 * call.
 *
 * Always one seat per click. A plus-one goes in as a SECOND NAMED GUEST rather
 * than as a party of two under one name, because a party of six under one name
 * gives the restaurant one name and five ghosts, and gives the host a headcount
 * they cannot call anybody about. Naming the plus-one is the whole point.
 */
export default function AddDinnerGuest({
  groupId,
  hold,
  roster,
  onSeated,
  onRosterChanged,
}: {
  groupId: string;
  hold: GroupDinnerHold;
  roster: GroupInvitee[];
  /** The table changed: re-read the holds. */
  onSeated: () => void;
  /** A guest was written to the trip: re-read the group. */
  onRosterChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<DinnerClaimNote | null>(null);

  const seatMutation = useMutation<DinnerClaimResult, unknown, SeatTarget>(
    (target) =>
      groupsApi.claimDinnerSpotForInvitee(groupId, hold.id, {
        inviteeId: target.inviteeId,
        partySize: 1,
      }),
    {
      onSuccess: (result, target) => {
        const outcome = dinnerClaimResultNote(result, target.guestName);
        setNote({
          ...outcome,
          message: [target.preface, outcome.message].filter(Boolean).join(' '),
        });
        // Re-read on every 2xx, not only the writes: `already_claimed` means the
        // list on screen is behind the table, which is the one case where the
        // host most needs the refresh.
        onSeated();
      },
      /*
        The reason this component does not use `mutationErrorMessage` here. Every
        refusal on this endpoint is a DinnerClaimResult body, which carries none
        of the keys that helper looks for, so it would render a 409 that names
        the conflicting restaurant as "Request failed with status code 409".
      */
      onError: (error, target) => setNote(dinnerClaimErrorNote(error, target.guestName)),
    },
  );

  const addGuestMutation = useMutation<AddOutcome, unknown, NewDinnerGuest>(
    async (guest) => {
      const rows: GroupInviteeInput[] = [
        { fullName: guest.fullName, email: guest.email, phone: guest.phone },
      ];

      // Preview first, always. Without a fingerprint the write runs the server's
      // own key-based matcher and may UPDATE a guest nobody approved; with one,
      // it resolves on the ids in the plan alone. Studio never matches guests.
      const preview = await groupsApi.previewInviteeImport(groupId, rows);
      const plan = dinnerGuestPlan(preview);

      if (plan.kind === 'needs_review') {
        return { rosterChanged: false, note: plainNote('warning', plan.message) };
      }
      if (plan.kind === 'existing') {
        return {
          rosterChanged: false,
          seat: {
            inviteeId: plan.inviteeId,
            guestName: plan.fullName,
            preface: `${plan.fullName} was already on this trip, so nobody new was added.`,
          },
        };
      }

      const written = await groupsApi.importInvitees(groupId, rows, preview.rosterFingerprint);
      const inviteeId = createdInviteeId(roster, written);
      if (!inviteeId) {
        return {
          rosterChanged: true,
          note: plainNote('warning', unseatedGuestNote(guest.fullName, written)),
        };
      }
      return { rosterChanged: true, seat: { inviteeId, guestName: guest.fullName } };
    },
    {
      onSuccess: (outcome) => {
        if (outcome.rosterChanged) onRosterChanged();
        if (outcome.note) setNote(outcome.note);
        if (outcome.seat) seatMutation.mutate(outcome.seat);
      },
      onError: (error, guest) =>
        setNote(
          plainNote(
            'error',
            mutationErrorMessage(error) || `Could not add ${guest.fullName} to this trip.`,
          ),
        ),
    },
  );

  const saving = seatMutation.isLoading || addGuestMutation.isLoading;
  const clearThenRun = (run: () => void) => {
    setNote(null);
    run();
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Button size="small" onClick={() => setOpen((prev) => !prev)}>
        {open ? 'Done adding' : 'Add a guest to this table'}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={1.5}>
          {note ? <Alert severity={note.severity}>{note.message}</Alert> : null}
          {/*
            Remount on any change to the table so the search box and the
            half-filled new-guest form clear once a seat lands. A FAILED attempt
            leaves the table alone, so what the host typed survives — which is
            the case where retyping actually costs them something.
          */}
          <DinnerGuestPicker
            key={hold.claims.length}
            roster={roster}
            claims={hold.claims}
            saving={saving}
            onSeatInvitee={(invitee) =>
              clearThenRun(() =>
                seatMutation.mutate({ inviteeId: invitee.id, guestName: invitee.fullName }),
              )
            }
            onAddGuest={(guest) => clearThenRun(() => addGuestMutation.mutate(guest))}
          />
        </Stack>
      </Collapse>
    </Box>
  );
}
